import React, {
	memo,
	useState,
	useCallback,
	useEffect,
	useRef,
	useMemo,
} from "react";
import getImgUrl from "../../../utils/getImgUrl";
import { motion } from "framer-motion";
import { BsChevronLeft as LeftArrow } from "react-icons/bs";
import { FaChevronDown as DownArrow } from "react-icons/fa";
import { BiChevronRight as RightArrow } from "react-icons/bi";
import { IoIosShareAlt as ShareIcon } from "react-icons/io";
import { MdClose as CrossIcon } from "react-icons/md";
import {
	BadgeButton,
	ExpandableContainer,
	InlineQuantityPicker,
	Loader,
	RoundedCard,
	StatusLabel,
} from "../../CoreUI";
import "./product-view-core.css";
import ProductVariantSelector from "../../CardComponents/ProductVariantSelector";
import anim from "../../../utils/anim";
import { DEFAULT_OFFER_IMAGE } from "../../../conf";
import GET from "../../../utils/GET";
import useTrack, { eventTypes } from "../../../hooks/useTrack";

const innerHeight = GET("innerHeight");
export const ProductViewRoundedCard = memo(
	({ className = "", children, ...props }) => {
		return (
			<RoundedCard
				className={`product-view-rounded-card ${className}`}
				variants={anim.pdpItem}
				{...props}
			>
				{children}
			</RoundedCard>
		);
	}
);
export const ProductViewExpandableCard = memo(
	({ children, className = "", ...props }) => {
		return (
			<ExpandableContainer
				className={`product-view-expandable-card ${className}`}
				{...props}
			>
				{children}
			</ExpandableContainer>
		);
	}
);

export const ProductViewBadgeContainer = memo(
	({ badgeData, remoteKey = "name" }) => {
		if (!Array.isArray(badgeData)) {
			return null;
		}
		return (
			<section className="product-view-badge-container">
				{badgeData.map((badge, index) => {
					return (
						<BadgeButton
							badgeItem
							key={`${badge[remoteKey]}_${index}`}
						>
							{badge[remoteKey]}
						</BadgeButton>
					);
				})}
			</section>
		);
	}
);

export const ProductViewSimilarProduct = memo(
	({
		product,
		handleProductClick,
		offsetTop,
		activeProduct,
		productViewEventProps,
		idx,
		...props
	}) => {
		if (!product) {
			return;
		}

		const eventTrackerRef = useRef(null);
		const productRef = useRef(null);
		const activeProductRef = useRef(JSON.stringify(activeProduct));
		const track = useTrack();
		const [fired, setFired] = useState(false);

		const handleTracking = useCallback(() => {
			if (!productRef?.current || !activeProductRef?.current) {
				return;
			}
			const productOffset = productRef.current.getBoundingClientRect().y;
			if (
				productOffset > offsetTop &&
				productOffset < offsetTop + Number(innerHeight) - 100
			) {
				if (!fired) {
					// console.log("Now tracked display for ", product.name);
					const {
						name,
						price,
						discounted_price,
						remote_id,
						id,
						brand_id,
						variants,
					} = product;
					const {
						remote_id: host_product_remote_id,
						name: host_product,
						id: host_product_id,
					} = JSON.parse(activeProductRef.current);

					track(eventTypes.WIDGET_PDP_SIMILAR_PRODUCT_DISPLAY, {
						host_product_id,
						host_product_remote_id,
						host_product,
						brand_id,
						product: name,
						product_id: id || "",
						product_remote_id: remote_id,
						price: price || "",
						discounted_price: discounted_price || "",
						variants: JSON.stringify(variants),
						similar_product_display_index: idx || -1,
					});
				}
				setFired(true);
			}
			// console.log(
			// 	`${product.name} offsetY is: `,
			// 	productRef.current.getBoundingClientRect().y,
			// 	" ------- range: [",
			// 	offsetTop,
			// 	", ",
			// 	offsetTop + Number(innerHeight),
			// 	"]"
			// );
		}, [offsetTop, product, fired, track, idx]);

		useEffect(() => {
			eventTrackerRef.current = setInterval(handleTracking, 800);

			return () => {
				clearInterval(eventTrackerRef.current);
			};
		}, [handleTracking]);

		useEffect(() => {
			if (!fired) {
				return;
			}
			// console.log("Clearing interval for product : ", product.name);
			clearInterval(eventTrackerRef.current);
		}, [fired, product]);
		// console.log(activeProduct.current === JSON.stringify(activeProduct));
		useEffect(() => {
			if (
				activeProductRef.current === JSON.stringify(activeProduct) ||
				!fired
			) {
				return;
			}
			setFired(false);
			eventTrackerRef.current = setInterval(handleTracking, 800);
			activeProductRef.current = JSON.stringify(activeProduct);
		}, [activeProduct, handleTracking, fired]);

		const _handleProductClick = () => {
			const {
				name,
				price,
				discounted_price,
				remote_id,
				id,
				variants,
			} = product;
			track(eventTypes.WIDGET_PDP_SIMILAR_PRODUCT_CLICK, {
				host_product_id: productViewEventProps.product_id,
				host_product_remote_id: productViewEventProps.product_remote_id,
				host_product_name: productViewEventProps.product,
				brand: productViewEventProps.brand,
				brand_id: productViewEventProps.brand_id,
				product: name,
				product_id: id || "",
				product_remote_id: remote_id,
				price: price || "",
				discounted_price: discounted_price || "",
				variants: JSON.stringify(variants),
				similar_product_display_index: idx || -1,
			});
			handleProductClick(product);
		};

		return (
			<section
				className="product-view-similar-product"
				onClick={_handleProductClick}
				ref={productRef}
				{...props}
			>
				<img src={getImgUrl(product)} alt="" />
				<div className="product-view-similar-product-details">
					<h3>{product.name}</h3>
					<div className="product-view-similar-product-detail-price">
						${product.discounted_price || product.price}
					</div>
				</div>
			</section>
		);
	}
);

const ProductViewNavBarButton = memo(
	({ children, className = "", transparent = false, ...props }) => {
		return (
			<div
				className={`${className} product-view-nav-bar-button ${
					transparent ? "product-view-nav-bar-button-transparent" : ""
				}`}
				{...props}
			>
				{children}
			</div>
		);
	}
);

export const ProductViewNavBar = memo(
	({ handleClose, product, productViewEventProps, ...props }) => {
		const track = useTrack();

		const handleShare = useCallback(() => {
			const shareTitle = "Checkout this cool product I found";
			const shareText = product.name;
			const shareLink = product.landing_page;
			let navigator_api = false;
			if (navigator.share !== undefined) {
				navigator_api = true;
				navigator
					.share({
						title: shareTitle,
						text: shareText,
						url: shareLink,
					})
					.catch((e) => {
						console.log("An error occurred while sharing", e);
					});
			} else {
				window.location = `mailto:?subject=${shareTitle}&body=${shareText}%0A%0A${shareLink}`;
			}
			track(eventTypes.WIDGET_PDP_SHARE, {
				...productViewEventProps,
				navigator_api,
			});
		}, [product, productViewEventProps, track]);

		return (
			<section className="product-view-nav-bar" {...props}>
				<div className="product-view-nav-bar-section">
					<ProductViewNavBarButton onClick={handleClose}>
						<LeftArrow />
					</ProductViewNavBarButton>
				</div>
				{product && (
					<div className="product-view-nav-bar-section">
						<ProductViewNavBarButton onClick={handleShare}>
							<ShareIcon />
						</ProductViewNavBarButton>
					</div>
				)}
			</section>
		);
	}
);

const ProductViewActionBarButton = memo(
	({ type = "theme", className = "", children, ...props }) => {
		return (
			<div
				className={`product-view-action-bar-button ${
					type === "dark" ? "product-view-action-bar-button-dark" : ""
				} ${className}`}
				{...props}
			>
				<div className="product-view-action-bar-button-children">
					{children}
				</div>
				<div className="product-view-action-bar-button-icon">
					<RightArrow />
				</div>
			</div>
		);
	}
);

const ProductViewActionBarDummy = memo(
	({ className = "", children, icon, ...props }) => {
		return (
			<section
				className={`product-view-action-bar-dummy ${className}`}
				{...props}
			>
				<div className="product-view-action-bar-dummy-text">
					{children || "Select details"}
				</div>
				<div className="product-view-action-bar-dummy-icon">
					{icon || <DownArrow />}
				</div>
			</section>
		);
	}
);

export const ProductViewActionBar = memo(
	({
		actionOverlay,
		handleActionOverlay,
		isSingleVariant,
		handlePurchase,
		quantity,
		setQuantity,
		maxQuantity,
		loading = false,
		productViewEventProps,
		...props
	}) => {
		const track = useTrack();
		const showPurchaseState = useMemo(() => {
			if (actionOverlay || isSingleVariant) {
				return true;
			}
			return false;
		}, [actionOverlay, isSingleVariant]);

		const handleButtonClick = useCallback(
			(dummyButtonClick = false) => {
				if (showPurchaseState) {
					return handlePurchase();
				}
				track(eventTypes.WIDGET_PDP_VARIANT_SCREEN_OPEN, {
					...productViewEventProps,
					text_click: dummyButtonClick,
				});
				return handleActionOverlay();
			},
			[
				showPurchaseState,
				handlePurchase,
				handleActionOverlay,
				track,
				productViewEventProps,
			]
		);

		const handleDummyButtonClick = useCallback(
			() => handleButtonClick(true),
			[handleButtonClick]
		);

		const handlePurchaseClick = useCallback(
			() => handleButtonClick(false),
			[handleButtonClick]
		);

		return (
			<section className="product-view-action-bar" {...props}>
				<section className="product-view-action-bar-item product-view-action-bar-item-left">
					{showPurchaseState ? (
						<InlineQuantityPicker
							quantity={quantity}
							setQuantity={setQuantity}
							maxQuantity={maxQuantity}
						/>
					) : (
						<ProductViewActionBarDummy
							onClick={handleDummyButtonClick}
						/>
					)}
				</section>
				<section className="product-view-action-bar-item product-view-action-bar-item-right">
					{loading ? (
						<Loader
							normal
							className="product-view-action-bar-loader"
						/>
					) : (
						<ProductViewActionBarButton
							type={showPurchaseState ? "theme" : "dark"}
							onClick={handlePurchaseClick}
						>
							{showPurchaseState ? "Buy Now" : "Purchase"}
						</ProductViewActionBarButton>
					)}
				</section>
			</section>
		);
	}
);

export const ProductViewActionOverlay = memo(
	({
		product,
		className = "",
		selectedVariant,
		setSelectedVariant,
		quantity,
		error,
		setError,
		handleClose,
		...props
	}) => {
		if (!product) {
			return;
		}

		const handleVariantChange = useCallback(
			(value) => {
				setError(false);
				setSelectedVariant(value);
			},
			[setError, setSelectedVariant]
		);

		return (
			<motion.section
				className={`product-view-action-overlay-container ${className}`}
				variants={anim.dialogQuick}
				initial="initial"
				animate="animate"
				exit="exit"
				{...props}
			>
				<ProductViewNavBarButton
					onClick={handleClose}
					className="product-view-action-overlay-dismiss-btn"
					variants={anim.pdpItem}
				>
					<CrossIcon />
				</ProductViewNavBarButton>
				<motion.img
					src={
						selectedVariant?.photo_url ||
						product.photo_url ||
						DEFAULT_OFFER_IMAGE
					}
					alt=""
					className="product-view-action-overlay-img"
					variants={anim.pdpItem}
				/>
				{/* <motion.div
					className="product-view-action-overlay-dismiss-btn"
					onClick={handleClose}
					variants={anim.pdpItem}
				>
					CLOSE
				</motion.div> */}
				<RoundedCard
					className="product-view-action-overlay-information"
					style={{ maxHeight: `${innerHeight / 2}px` }}
					variants={anim.pdpItem}
				>
					<section className="product-view-action-overlay-information-head">
						<h2 className="product-view-action-overlay-title">
							{product.name}
						</h2>
						<div className="product-view-action-overlay-price">
							${product.discounted_price || product.price}
						</div>
						{error && <StatusLabel>{error}</StatusLabel>}
					</section>

					<section className="product-view-action-overlay-variant-selector">
						<ProductVariantSelector
							variantData={product.variants}
							handleVariantChange={handleVariantChange}
							variants={anim.dialogItem}
						/>
					</section>
				</RoundedCard>
			</motion.section>
		);
	}
);
